import React, { useState, useEffect } from "react";
import industryDataset from "./data/IndustryDataset";
import "../styles/steps.css"; // Import styles from steps.css
import { resetResourceTable } from '../../utils/helpers';

const SprintMilestoneDeliverables = ({ formData, updateFormData }) => {
    const [sprintsMilestones, setSprintsMilestones] = useState(formData.sprintsMilestones || []);
    const [sprintDuration, setSprintDuration] = useState(2); // Default sprint duration
    const [customDeliverable, setCustomDeliverable] = useState("");
    const [customDuration, setCustomDuration] = useState(2);
    const [error, setError] = useState("");

    // Initialize deliverables based on industry and project type
    useEffect(() => {
        if (formData.industry && industryDataset[formData.industry] && !formData.sprintsMilestones.length) {
            const industryData = industryDataset[formData.industry];
            const defaultSprintsMilestones = industryData.sprintsOrMilestones.map((item) => ({
                name: item.name,
                duration: formData.implementationType === "Agile" ? sprintDuration : item.duration, // Apply sprint duration rule
            }));
            setSprintsMilestones(defaultSprintsMilestones);
            updateFormData({ sprintsMilestones: defaultSprintsMilestones });
            updateFormData({
                allocationMatrix: [],
                resourceTable: [],
                resourceAllocationGrid: []
            });

        }
    }, [formData.industry, formData.implementationType, sprintDuration, formData.sprintsMilestones, updateFormData]);

    // Update formData whenever sprintsMilestones change
    useEffect(() => {
        if (formData.sprintsMilestones !== sprintsMilestones) {
            updateFormData({ sprintsMilestones });
            updateFormData({
                allocationMatrix: [],
                resourceTable: [],
                resourceAllocationGrid: []
            });
        }
    }, [sprintsMilestones, formData.sprintsMilestones, updateFormData]);

    const handleDurationChange = (index, value) => {
        const duration = parseInt(value, 10);
        if (isNaN(duration) || duration < 1) {
            setError("Duration must be a valid number greater than zero.");
            return;
        }
        setError("");

        const updatedSprintsMilestones = [...sprintsMilestones];

        if (formData.implementationType === "Agile") {
            // If Agile, update all sprint durations to match the first one
            updatedSprintsMilestones.forEach((d) => (d.duration = duration));
            setSprintDuration(duration);
        } else {
            // If Waterfall, only update the specific milestone's duration
            updatedSprintsMilestones[index].duration = duration;
        }

        setSprintsMilestones(updatedSprintsMilestones);
    };

    const handleAddDeliverable = () => {
        if (!customDeliverable) {
            setError(`Enter a ${formData.implementationType === "Agile" ? "Sprint" : "Milestone"} name before adding.`);
            return;
        }
        setError("");

        const newSprintMilestone = {
            name: customDeliverable,
            duration: formData.implementationType === "Agile" ? sprintDuration : customDuration, // Apply duration rule
        };

        const updatedSprintsMilestones = [...sprintsMilestones, newSprintMilestone];
        setSprintsMilestones(updatedSprintsMilestones);
        setCustomDeliverable("");
        setCustomDuration(2); // Reset duration input
    };

    const handleRemoveDeliverable = (index) => {
        const updatedSprintsMilestones = sprintsMilestones.filter((_, i) => i !== index);
        setSprintsMilestones(updatedSprintsMilestones);
    };

    const handleNameChange = (index, value) => {
        const updatedSprintsMilestones = [...sprintsMilestones];
        updatedSprintsMilestones[index].name = value;
        setSprintsMilestones(updatedSprintsMilestones);
    };

    return (
        <div className="step-container">
            <p className="step-description">
                {formData.implementationType} Planning - Provide as many {formData.implementationType === "Agile" ? "sprints" : "milestones"} as possible—this helps Soft Estimate determine the overall project timeline.
                You can switch between Agile (sprint-based) or Waterfall (milestone-based) anytime without losing progress.
                <br /><br />If you choose Agile, all sprints should have a similar duration, typically between 1 to 3 weeks. In Waterfall, each milestone can have a different duration based on project needs.
            </p>

            {error && <p className="error">{error}</p>}

            {sprintsMilestones.map((sprintMilestone, index) => (
                <div key={index} className="deliverable-card">
                    <input
                        type="text"
                        value={sprintMilestone.name}
                        placeholder={`${formData.implementationType === "Agile" ? "Sprint" : "Milestone"} ${index + 1} Objective`}
                        className="deliverable-input"
                        onChange={(e) => handleNameChange(index, e.target.value)}
                    />

                    <div className="duration-container">
                        <input
                            type="number"
                            min="1"
                            max={formData.implementationType === "Agile" ? "3" : "10"}
                            value={sprintMilestone.duration}
                            onChange={(e) => handleDurationChange(index, e.target.value)}
                            className="duration-input"
                        />
                        <span className="unit-label">wks</span>
                    </div>

                    <button
                        onClick={() => handleRemoveDeliverable(index)}
                        className="delete-button"
                        type="button" // Ensure the button doesn't submit a form
                    >
                        ❌
                    </button>
                </div>
            ))}

            {/* Add New Sprint/Milestone Section */}
            <div className="add-deliverable">
                <input
                    type="text"
                    placeholder={`Enter new ${formData.implementationType === "Agile" ? "Sprint" : "Milestone"}`}
                    value={customDeliverable}
                    onChange={(e) => setCustomDeliverable(e.target.value)}
                    className="deliverable-input"
                />

                <input
                    type="number"
                    min="1"
                    max={formData.implementationType === "Agile" ? "3" : "10"}
                    value={customDuration}
                    onChange={(e) => setCustomDuration(parseInt(e.target.value) || 2)}
                    className="duration-input"
                />

                <button
                    onClick={handleAddDeliverable}
                    className="add-button"
                    type="button" // Ensure the button doesn't submit a form
                >
                    + Add {formData.implementationType === "Agile" ? "Sprint" : "Milestone"}
                </button>
            </div>
        </div>
    );
};

export default SprintMilestoneDeliverables;
